import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listOperationalRecordings, listOperationalUploadParticipants, operationalKeys } from "@rekonas/data-access";
import type { OperationalRecordingSummary } from "@rekonas/domain";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { UploadPanel } from "../components/upload/UploadPanel";
import { useOperationalWorkspace } from "../operationalWorkspace";

export function shouldExposeWebUploads(flag: string | undefined, permissions: readonly string[]): boolean {
  return flag === "1" && permissions.includes("recordings.upload");
}

export function WebUploadGate(props: { flag: string | undefined; permissions: readonly string[]; children: ReactNode }) {
  return shouldExposeWebUploads(props.flag, props.permissions) ? props.children : null;
}

export function operationalRecordingPath(recordingRef: string): string {
  return `/recordings/${encodeURIComponent(recordingRef)}`;
}

function formatDate(value: string | number | null): string {
  if (value == null) return "Date pending";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  return Number.isNaN(date.valueOf()) ? "Date pending" : new Intl.DateTimeFormat(undefined, {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  }).format(date);
}

function formatDuration(value: number | null): string {
  if (!value) return "Pending";
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function productStatus(recording: OperationalRecordingSummary): { label: string; tone: string; viewer: boolean } {
  if (recording.signals !== "available") return { label: "Processing signals", tone: "processing", viewer: false };
  if (recording.hypnogram === "failed") return { label: "Signals ready · sleep failed", tone: "attention", viewer: true };
  if (recording.hypnogram === "processing" || recording.hypnogram === "queued") {
    return { label: "Signals ready · sleep processing", tone: "processing", viewer: true };
  }
  if (recording.viewer !== "available") return { label: "Preparing viewer", tone: "processing", viewer: false };
  return { label: "Ready", tone: "ready", viewer: true };
}

function RecordingRow({ recording }: { recording: OperationalRecordingSummary }) {
  const status = productStatus(recording);
  return (
    <article className="operational-recording-row">
      <div className="recording-date">
        <span>Night recording</span>
        <strong>{formatDate(recording.display_date)}</strong>
        <small>{recording.recording_ref}</small>
      </div>
      <div><span className="row-label">Duration</span><strong>{formatDuration(recording.duration_seconds)}</strong></div>
      <div><span className="row-label">Devices</span><strong>{recording.device_summary.join(" · ") || "Pending"}</strong></div>
      <div><span className="row-label">Status</span><span className={`product-status status-${status.tone}`}>{status.label}</span></div>
      <div className="recording-action">
        {status.viewer ? <Link to={operationalRecordingPath(recording.recording_ref)}>Open data <span aria-hidden="true">→</span></Link>
          : <span aria-disabled="true">Data processing</span>}
      </div>
    </article>
  );
}

export function RecordingsPage() {
  const [toolsOpen, setToolsOpen] = useState(false);
  const workspaces = useOperationalWorkspace();
  const workspaceRef = workspaces.workspace?.slug || "";
  const recordings = useQuery({
    queryKey: operationalKeys.list(workspaceRef),
    queryFn: ({ signal }) => listOperationalRecordings(workspaceRef, signal),
    enabled: Boolean(workspaceRef),
    refetchInterval: (query) => query.state.data?.items.some((item) =>
      item.signals !== "available" || item.hypnogram === "processing" || item.hypnogram === "queued") ? 5_000 : false,
  });
  const webUploads = shouldExposeWebUploads(
    import.meta.env.VITE_OPERATIONAL_WEB_UPLOADS,
    workspaces.workspace?.permissions || [],
  );
  const uploadParticipants = useQuery({
    queryKey: operationalKeys.uploadParticipants(workspaceRef),
    queryFn: ({ signal }) => listOperationalUploadParticipants(workspaceRef, signal),
    enabled: Boolean(workspaceRef && webUploads),
  });
  useEffect(() => { document.title = "Night recordings · Rekonas"; }, []);

  const items = recordings.data?.items || [];
  const ready = items.filter((item) => productStatus(item).viewer).length;
  const processing = items.length - items.filter((item) => item.state === "ready" || item.state === "attention_required").length;

  if (workspaces.isPending) return <main className="operational-loading"><Spinner /> Loading authorized workspace…</main>;
  if (workspaces.isError || workspaces.authorizedCount !== 1 || !workspaces.workspace) return (
    <main className="operational-main"><Alert>
      {workspaces.authorizedCount > 1
        ? "This account has more than one operational workspace. Choose a workspace context before opening recordings."
        : "No authorized operational workspace is available for this account."}
    </Alert></main>
  );
  return (
    <main className="operational-main">
      <section className="operational-hero">
        <div><p>Operational dashboard</p><h1>Night recordings</h1><span>Review available signals and sleep analysis for authorized recordings.</span></div>
        <dl><div><dt>Recordings</dt><dd>{items.length}</dd></div><div><dt>Viewable</dt><dd>{ready}</dd></div><div><dt>Processing</dt><dd>{processing}</dd></div></dl>
      </section>
      <section className="recording-list-shell">
        <header><div><p>Authorized scope</p><h2>{workspaces.workspace.display_name}</h2></div>
          <div className="recording-list-actions">
            <WebUploadGate flag={import.meta.env.VITE_OPERATIONAL_WEB_UPLOADS} permissions={workspaces.workspace.permissions}>
              <Button size="sm" variant="secondary" aria-expanded={toolsOpen} onClick={() => setToolsOpen((open) => !open)}>Test tools</Button>
            </WebUploadGate>
            <Button size="sm" variant="ghost" onClick={() => void recordings.refetch()} disabled={recordings.isFetching}>{recordings.isFetching && <Spinner />} Refresh</Button>
          </div>
        </header>
        {webUploads && toolsOpen && uploadParticipants.isPending &&
          <div className="operational-loading"><Spinner /> Loading participant binding…</div>}
        {webUploads && toolsOpen && uploadParticipants.isError &&
          <Alert className="recording-list-alert">Upload participant binding could not be loaded.</Alert>}
        {webUploads && toolsOpen && uploadParticipants.isSuccess &&
          <UploadPanel workspaceRef={workspaceRef} participantRefs={uploadParticipants.data.participant_refs} />}
        {recordings.isPending && <div className="operational-loading"><Spinner /> Loading recordings…</div>}
        {recordings.isError && <Alert className="recording-list-alert">Recordings could not be loaded. Access is concealed when authorization is absent.</Alert>}
        {recordings.isSuccess && items.length === 0 && <div className="recording-empty"><h3>No recordings yet</h3><p>Accepted nightly recordings will appear here while processing.</p></div>}
        {items.map((recording) => <RecordingRow key={recording.recording_ref} recording={recording} />)}
      </section>
    </main>
  );
}
